import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [externalAuthError, setExternalAuthError] = useState('');
  const [interfaceSettings, setInterfaceSettings] = useState({
    logo_url: null,
    primary_color: '#ff6b35',
    secondary_color: '#ff8c5a',
    accent_color: '#ffa07a',
  });
  const { login, externalLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const EMBED_ALLOWED_ORIGINS = [
    'https://legal.novacutan.ru',
    'https://www.legal.novacutan.ru',
  ];

  useEffect(() => {
    const fetchInterface = async () => {
      try {
        const res = await axios.get('/api/settings/interface/');
        setInterfaceSettings(res.data);
      } catch {
        try {
          const res = await axios.get('/api/health/interface/');
          setInterfaceSettings(res.data);
        } catch (e) {
          console.error('Ошибка загрузки настроек интерфейса', e);
        }
      }
    };
    fetchInterface();
  }, []);

  useEffect(() => {
    if (!interfaceSettings.primary_color) return;
    document.documentElement.style.setProperty('--primary-color', interfaceSettings.primary_color);
    if (interfaceSettings.secondary_color) {
      document.documentElement.style.setProperty('--secondary-color', interfaceSettings.secondary_color);
    }
  }, [interfaceSettings.primary_color, interfaceSettings.secondary_color]);

  const resolvePostMessageTargetOrigin = () => {
    const explicitParentOrigin = (searchParams.get('parent_origin') || '').trim();
    if (explicitParentOrigin && EMBED_ALLOWED_ORIGINS.includes(explicitParentOrigin)) {
      return explicitParentOrigin;
    }
    const ref = (document.referrer || '').trim();
    if (!ref) return null;
    try {
      const origin = new URL(ref).origin;
      if (EMBED_ALLOWED_ORIGINS.includes(origin)) {
        return origin;
      }
      return null;
    } catch {
      return null;
    }
  };

  const postEmbedAuthEvent = (eventName, payload = {}) => {
    const targetOrigin = resolvePostMessageTargetOrigin();
    if (!targetOrigin || !window.parent || window.parent === window) return;
    window.parent.postMessage(
      {
        type: eventName,
        source: 'archiveweb',
        payload,
      },
      targetOrigin
    );
  };

  useEffect(() => {
    const externalJwt =
      searchParams.get('external_jwt') ||
      searchParams.get('jwt') ||
      searchParams.get('auth_token');
    if (!externalJwt) return;
    let cancelled = false;
    setExternalAuthError('');

    const runExternalLogin = async () => {
      setLoading(true);
      const result = await externalLogin(externalJwt);
      if (cancelled) return;
      if (result.success) {
        postEmbedAuthEvent('archiveweb-auth-success', {
          user_id: result.user?.id || null,
          role: result.user?.role || null,
        });
        postEmbedAuthEvent('archiveweb-auth-ok', {
          user_id: result.user?.id || null,
          role: result.user?.role || null,
        });
        if (result.user?.role === 'client') {
          toast.success('Вход выполнен успешно', { autoClose: 1000 });
        } else {
          toast.success('Вход выполнен успешно');
        }
        navigate('/webinars', { replace: true });
      } else {
        const message = result.error || 'Ошибка внешней авторизации';
        postEmbedAuthEvent('archiveweb-auth-error', { error: message });
        setExternalAuthError(message);
        toast.error(message);
      }
      setLoading(false);
    };

    runExternalLogin();
    return () => {
      cancelled = true;
    };
  }, [externalLogin, navigate, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      if (result.user?.role === 'client') {
        toast.success('Вход выполнен успешно', { autoClose: 1000 });
      } else {
        toast.success('Вход выполнен успешно');
      }
      navigate('/webinars');
    } else {
      toast.error(result.error || 'Ошибка входа');
    }

    setLoading(false);
  };

  const loginStyle = {
    '--login-primary': interfaceSettings.primary_color || '#ff6b35',
    '--login-secondary': interfaceSettings.secondary_color || '#ff8c5a',
    '--login-accent': interfaceSettings.accent_color || '#ffa07a',
    background: `linear-gradient(135deg, ${interfaceSettings.primary_color || '#ff6600'} 0%, ${interfaceSettings.secondary_color || '#ff8533'} 100%)`,
  };

  return (
    <div className="login-container" style={loginStyle}>
      <div className="login-card">
        <div className="login-header">
          {interfaceSettings.logo_url ? (
            <img src={interfaceSettings.logo_url} alt="Логотип" className="login-logo" />
          ) : (
            <h1>Webinar AI</h1>
          )}
          <p>Платформа управления видеоконтентом</p>
        </div>
        {!!externalAuthError && (
          <div className="error-message" style={{ marginBottom: '12px' }}>
            Ошибка внешнего входа: {externalAuthError}
          </div>
        )}
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Пароль</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Вход...' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
